import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, NgZone, PLATFORM_ID, Renderer2, RendererFactory2 } from '@angular/core';

import { EcmDispatcherModule, EnvUtils } from '@ecm/dispatcher';

import { ElcosParams } from '../business/elcos/elcos-params';
import { PropertiesUtils } from '../utils/properties.utils';

/**
 * Enum used to list possible brands
 */
export enum Brands {
    ORANGE = 'gp',
    SOSH = 'sosh',
    PRO = 'pro',
}

enum ElementsIds {
    ORANGE_HEADER_ID = 'o-header',
    SOSH_HEADER_ID = 's-header',
    SOSH_FOOTER_ID = 'o-footer-content-sosh',
}

/**
 * * @see {@link https://elements-communs.pages.gitlab.si.francetelecom.fr/doc/onei/integration/}
 */
@Injectable({
    providedIn: 'root',
})
export class ElcosService {
    /**
     * Class name used for console messages
     * @type {string}
     */
    private static readonly CLASS_NAME: string = 'ElcosService';

    /**
     * Static property to store the last displayed brand and conf to avoid calling refresh with same datas
     * @type {string}
     */
    private static displayedConf: string;

    /**
     * Static property to store the current displayed brand and conf, usage when we programmatically change elcos
     * @type {string}
     */
    private static storedCurrentConf: string;

    /**
     * Renderer used to show/hide elcos
     * @type {Renderer2}
     */
    private renderer: Renderer2;

    private readonly defaultOrangeConfig = {
        highlightPath: ['care'],
        genericHeaderZone: false,
        footer: {
            syndicationZone: true,
            sitemapZone: true,
        },
        display: {
            persist: 0,
        },
    };

    /**
     * Generate a cache key from the brand and updated o_confParam.
     * Be careful, the key is built from JSON.stringify, so the order of node in object is important
     * @param {Brands} brand
     * @param {any} params
     */
    private static getConfKey(brand: Brands, params: any) {
        return brand + ';' + JSON.stringify(params);
    }

    public constructor(
        @Inject(PLATFORM_ID) private platformId: any,
        private zone: NgZone,
        rendererFactory: RendererFactory2,
    ) {
        ElcosService.displayedConf = ElcosService.displayedConf || '';
        ElcosService.storedCurrentConf = ElcosService.storedCurrentConf || '';
        this.renderer = rendererFactory.createRenderer(null, null);
    }

    public storeCurrentElcosConfig(): void {
        if (ElcosService.displayedConf !== '') {
            ElcosService.storedCurrentConf = ElcosService.displayedConf;
            EcmDispatcherModule.sendConsoleInfo(
                ElcosService.CLASS_NAME,
                'stored current elcos - ' + ElcosService.displayedConf,
            );
        }
    }

    public shown(): boolean {
        return ElcosService.displayedConf !== '';
    }

    public switchToOrange(): void {
        this.callRefreshMethod(Brands.ORANGE, this.defaultOrangeConfig);
    }

    /**
     * @deprecated : This method will be removed, please use `switchToOrange`instead
     */
    public switchToOrangeOne(): void {
        this.switchToOrange();
    }

    public switchToOrangeReunion(): void {
        this.callRefreshMethod(Brands.ORANGE, {
            ...this.defaultOrangeConfig,
            display: {
                persist: 4,
            },
        });
    }
    public switchToOrangeCaribbean(): void {
        this.callRefreshMethod(Brands.ORANGE, {
            ...this.defaultOrangeConfig,
            display: {
                persist: 3,
            },
        });
    }
    public switchToOrangeMayotte(): void {
        this.callRefreshMethod(Brands.ORANGE, {
            ...this.defaultOrangeConfig,
            display: {
                persist: 5,
            },
        });
    }

    /**
     * Switch Elcos (orange.fr common elements) to Orange Assistance style
     */
    public switchToAssistanceOrange(): void {
        this.callRefreshMethod(Brands.ORANGE, {
            highlightPath: ['care'],
            isCaraibe: false,
            domainTitle: 'Aide et Contact',
            domainUrl: 'https://assistance.orange.fr',
            persoZone: true,
            launchZone: true,
        });
    }

    /**
     * Switch Elcos (orange.fr common elements) to Assistance Pro style
     */
    public switchToAssistancePro(): void {
        this.callRefreshMethod(Brands.PRO, {
            highlightPath: ['assistance'],
            OPUSMode: true,
            domainTitle: 'assistance pro',
            domainUrl: 'https://assistance.orange.fr/accueil-pro',
            domainImg: {
                type: 'html',
                src: `<div id="polarisHeader">
                    <span class="service">
                        <a href="https://assistance.orange.fr/accueil-pro">assistance pro</a>
                    </span>
                    <span class="universe">
                        <a href="/" title="Cliquez pour passer en mode résidentiel"
                        onclick="reloadPageWithParam({'type': 'RES'}); return false;">particuliers</a>
                        &nbsp;|&nbsp;<span class="selected">professionnels</span>
                    </span>
                </div>`,
            },
        });
    }

    /**
     * Switch Elcos (orange.fr common elements) to Assistance Caribbean style
     */
    public switchToAssistanceCaribbean(): void {
        this.callRefreshMethod(Brands.ORANGE, {
            highlightPath: ['care'],
            domainTitle: 'Assistance',
            domainUrl: 'https://assistance.orange.fr',
            domainImg: '',
            persoZone: false,
            launchZone: false,
            isCaraibe: true,
        });
    }

    /**
     * Switch Elcos (orange.fr common elements) to Sosh style
     */
    public switchToSosh(): void {
        if ((<any>window).o_confCommon) {
            // the menu ID is different depending on the UA (mobile or desktop)
            const conf = (<any>window).o_confCommon.responsiveMobile === true ? ['menu5'] : ['espace-client'];
            this.callRefreshMethod(Brands.SOSH, {
                highlightPath: conf,
                footer: {
                    syndicationZone: false,
                    sitemapZone: false,
                },
            });
        }
    }

    /**
     * Switch Elcos (orange.fr common elements) to Neutral FAI V2
     * @param {string} titlePage custom title page to update the o_confCommon
     */
    public switchToOrangeNeutralFaiV2(titlePage: string): void {
        this.callRefreshMethod(Brands.ORANGE, {
            display: {
                persist: 7,
            },
            pageTitle: titlePage,
        });
    }

    /**
     * Switch Elcos (orange.fr common elements) to Commande
     */
    public switchToOrangCommande(): void {
        this.callRefreshMethod(Brands.ORANGE, {
            display: {
                persist: 8,
            },
        });
    }

    /**
     * Switch Elcos (orange.fr common elements) to storage elcos
     */
    public switchToSavedElcos(): void {
        const [brand, config] = ElcosService.storedCurrentConf.split(';');
        this.callRefreshMethod(brand as Brands, JSON.parse(config));
    }

    /**
     * Switch Elcos (orange.fr common elements) to Sosh style
     */
    public switchToAssistanceSosh(): void {
        this.callRefreshMethod(Brands.SOSH, {
            highlightPath: [],
        });
    }

    /**
     * Show "Espace Client" Header for Orange account
     * @param {boolean} showHeader Show/Hide genericHeaderZone
     */
    public showGenericHeaderZone(showHeader: boolean): void {
        this.callRefreshMethod(
            Brands.ORANGE,
            {
                genericHeaderZone: showHeader,
            },
            true,
        );
    }

    /**
     * Hide Elcos header and footer
     */
    public hide(): void {
        this.hideHeader();
        this.hideFooter();
    }

    /**
     * Show elcos header and footer
     */
    public show(): void {
        this.showHeader();
        this.showFooter();
    }

    /**
     * Facade for the o_refresh method, used to avoid code duplication (method existence test)
     * @param {Brands} brand
     * @param {any} additionalParams to update the o_confCommon
     * @param {boolean} force to force the refresh of the elcos even if the brand don't change
     */
    private callRefreshMethod(brand: Brands, additionalParams: any, force = false): void {
        if (isPlatformBrowser(this.platformId) && globalThis.o_refresh && !EnvUtils.isWebview()) {
            const key = ElcosService.getConfKey(brand, additionalParams);
            try {
                if (ElcosService.displayedConf !== key || force) {
                    ElcosService.displayedConf = key;
                    EcmDispatcherModule.sendConsoleInfo(ElcosService.CLASS_NAME, 'o_refresh - ' + key);
                    this.zone.runOutsideAngular(() => {
                        globalThis.head.ready(() => globalThis.o_refresh(brand, additionalParams));
                    });
                }
            } catch (e) {
                EcmDispatcherModule.sendConsoleError(
                    ElcosService.CLASS_NAME,
                    'An error occurred while calling o_refresh : ' + e,
                );
            }
        }
    }

    /**
     * Hide element commun header. DOM manipulation because of asynchronous problem with orefresh call
     */
    private hideHeader(): void {
        PropertiesUtils.isSoshBrand() ? this.hideSoshHeader() : this.hideOrangeHeader();
    }

    /**
     * Hide Orange header
     */
    private hideOrangeHeader(): void {
        this.display(ElementsIds.ORANGE_HEADER_ID, false, 'Orange header not hidden');
    }

    /**
     * Hide Sosh header
     */
    private hideSoshHeader(): void {
        this.display(ElementsIds.SOSH_HEADER_ID, false, 'Sosh header not hidden');
    }

    /**
     * Hide element commun footer. DOM manipulation because of no functionality available
     */
    private hideFooter(): void {
        PropertiesUtils.isSoshBrand() ? this.hideSoshFooter() : this.hideOrangeFooter();
    }

    /**
     * Hide Orange footer
     */
    private hideOrangeFooter(): void {
        if (this.hasFooterId()) {
            this.display(this.getFooterId(), false, 'Footer not hidden');
        }
    }

    /**
     * Hide Sosh footer
     */
    private hideSoshFooter(): void {
        this.display(ElementsIds.SOSH_FOOTER_ID, false, 'Footer not hidden');
    }

    /**
     * Show element commun header. DOM manipulation because of asynchronous problem with orefresh call
     */
    private showHeader(): void {
        PropertiesUtils.isSoshBrand() ? this.showSoshHeader() : this.showOrangeHeader();
    }

    /**
     * Show Orange header
     */
    private showOrangeHeader(): void {
        this.display(ElementsIds.ORANGE_HEADER_ID, true, 'Orange header not displayed');
    }

    /**
     * Show Sosh header
     */
    private showSoshHeader(): void {
        this.display(ElementsIds.SOSH_HEADER_ID, true, 'Sosh header not displayed');
    }

    /**
     * Show element commun footer. DOM manipulation because of no functionality available
     */
    private showFooter(): void {
        PropertiesUtils.isSoshBrand() ? this.showSoshFooter() : this.showOrangeFooter();
    }

    /**
     * Show Orange footer
     */
    private showOrangeFooter(): void {
        if (this.hasFooterId()) {
            this.display(this.getFooterId(), true, 'Footer not displayed');
        }
    }

    /**
     * Show Sosh footer
     */
    private showSoshFooter(): void {
        this.display(ElementsIds.SOSH_FOOTER_ID, true, 'Footer not displayed');
    }

    /**
     * Show or hide element corresponding to id sent
     * @param {string} id
     * @param {boolean} isDisplay
     * @param {string} errorMessage
     */
    private display(id: string, isDisplay: boolean, errorMessage: string): void {
        const windowRef = <any>window;
        const displayValue: string = isDisplay ? 'block' : 'none';
        if (windowRef.head) {
            windowRef.head.ready(() => {
                try {
                    const element: any = this.renderer.selectRootElement(`#${id}`, true);
                    this.renderer.setStyle(element, 'display', displayValue);
                } catch (e) {
                    EcmDispatcherModule.sendConsoleError(ElcosService.CLASS_NAME, `${errorMessage} : ${e}`);
                }
            });
        }
    }

    /**
     * Indicates if Conf common has a footer id
     * @returns {boolean}
     */
    private hasFooterId(): boolean {
        const windowRef = <any>window;
        return !!windowRef.o_confCommon && !!windowRef.o_confCommon.footer && !!windowRef.o_confCommon.footer.id;
    }

    /**
     * Returns footer id
     * @returns {string}
     */
    private getFooterId(): string {
        return (<any>window).o_confCommon.footer.id;
    }

    /**
     * enable tunnel mode for the given brand (changes the header and footer to display in a "tunnel" mode)
     * @param brand
     * @param params
     * @param headerOnly
     */
    public enableTunnel(brand: Brands, params: ElcosParams = {}, headerOnly = true): void {
        this.storeCurrentElcosConfig();
        const brandFlags = this.getBrandFlags(brand);
        const tunnelParams: any = {
            ...params,
            isTunnel: true,
            ...brandFlags,
        };

        if (headerOnly) {
            tunnelParams.footer = {
                ...(tunnelParams.footer ?? {}),
                syndicationZone: false,
                sitemapZone: false,
            };
        }

        this.callRefreshMethod(brand, tunnelParams, true);

        if (headerOnly) {
            this.hideFooter();
        }
    }

    /**
     * Restore the previously stored ELCOS config (header+footer+brand)
     */
    public disableTunnelAndRestore(): void {
        if (ElcosService.storedCurrentConf) {
            this.switchToSavedElcos();
        }

        this.showFooter();
    }

    /**
     * Returns brand flags (true/false for isSosh/isPro/isOrange)
     * @param brand
     * @private
     */
    private getBrandFlags(brand: Brands): Record<string, true> {
        switch (brand) {
            case Brands.SOSH:
                return { isSosh: true };
            case Brands.PRO:
                return { isPro: true };
            default:
                return { isOrange: true };
        }
    }
}
